import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companiesService } from '../services/companies.service';
import { useToast } from '@/components/ui';
import type { Company } from '../types';

export function useCompanies(filters: { search?: string; plan?: string } = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const companiesQuery = useQuery({
    queryKey: ['companies', filters],
    queryFn: async () => {
      const res = await companiesService.list(filters);
      if (res.error) throw new Error(res.error);
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => companiesService.delete(id),
    onSuccess: (res) => {
      if (res.error) {
        toast.error(res.error);
      } else {
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        toast.success('Xóa công ty thành công');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Lỗi khi xóa công ty');
    },
  });

  const upsertMutation = useMutation({
    mutationFn: (payload: Partial<Company>) => companiesService.upsert(payload),
    onSuccess: (res) => {
      if (res.error) {
        toast.error(res.error);
      } else {
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        toast.success('Lưu thông tin công ty thành công');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Lỗi khi lưu thông tin công ty');
    },
  });

  const lockMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => companiesService.lock(id, reason),
    onSuccess: (res) => {
      if (res.error) {
        toast.error(res.error);
      } else {
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        toast.success('Đã khóa công ty thành công');
      }
    },
  });

  const unlockMutation = useMutation({
    mutationFn: (id: string) => companiesService.unlock(id),
    onSuccess: (res) => {
      if (res.error) {
        toast.error(res.error);
      } else {
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        toast.success('Đã mở khóa công ty thành công');
      }
    },
  });

  const activateMutation = useMutation({
    mutationFn: ({ id, options }: { id: string; options?: any }) => companiesService.activate(id, options),
    onSuccess: (res) => {
      if (res.error) {
        toast.error(res.error);
      } else {
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        toast.success('Đã kích hoạt tổ chức thành công');
      }
    },
  });

  return {
    companies: companiesQuery.data || [],
    isLoading: companiesQuery.isLoading,
    isDeleting: deleteMutation.isPending,
    isSaving: upsertMutation.isPending,
    isLocking: lockMutation.isPending,
    isUnlocking: unlockMutation.isPending,
    isActivating: activateMutation.isPending,
    deleteCompany: deleteMutation.mutateAsync,
    upsertCompany: upsertMutation.mutateAsync,
    lockCompany: lockMutation.mutateAsync,
    unlockCompany: unlockMutation.mutateAsync,
    activateCompany: activateMutation.mutateAsync,
    error: companiesQuery.error,
    refetch: companiesQuery.refetch,
  };
}

export function useCompanyDetail(id: string | undefined) {
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['company', id],
    queryFn: async () => {
      if (!id) return null;
      const res = await companiesService.getById(id);
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    enabled: !!id,
  });

  const usageQuery = useQuery({
    queryKey: ['company', id, 'usage'],
    queryFn: async () => {
      if (!id) return null;
      const res = await companiesService.getUsageSummary(id);
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    enabled: !!id,
  });

  const subscriptionsQuery = useQuery({
    queryKey: ['company', id, 'subscriptions'],
    queryFn: async () => {
      if (!id) return null;
      const res = await companiesService.getSubscriptions(id);
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    enabled: !!id,
  });

  const projectsQuery = useQuery({
    queryKey: ['company', id, 'projects'],
    queryFn: async () => {
      if (!id) return null;
      const res = await companiesService.getProjects(id);
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    enabled: !!id,
  });

  const usersQuery = useQuery({
    queryKey: ['company', id, 'users'],
    queryFn: async () => {
      if (!id) return null;
      const res = await companiesService.getUsers(id);
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    company: detailQuery.data,
    usage: usageQuery.data,
    subscriptions: subscriptionsQuery.data || [],
    projects: projectsQuery.data || [],
    users: usersQuery.data || [],
    isLoading: detailQuery.isLoading || usageQuery.isLoading || subscriptionsQuery.isLoading || projectsQuery.isLoading || usersQuery.isLoading,
    error: detailQuery.error || usageQuery.error,
    refetchAll: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] });
    }
  };
}
